import { BloodworkAnalysis } from './BloodworkAnalysis';
import {
  PATTERN_A_FLOOR_NM,
  LDL_P_COUNT_SPLIT,
  LDL_P_SCALE_EDGES,
  SMALL_LDL_P_SPLIT,
  ldlPScalePercent,
  ldlPBandLabel,
} from './LipidParticleMap';

/**
 * The five Lipid Particle areas, their likelihood ladder, and the 21 patterns.
 *
 * Mirrors the Thyroid panel's shape (five areas, a band per area, patterns that
 * can flag an area to highly likely) with the extensions the Liver spec argued
 * for: a pattern here can also HOLD an area down, or be a NOTE that changes no
 * band at all. Three of the holds exist to talk a red bar DOWN, which is the
 * thing this section needs that Thyroid never did — see lipid-patterns-spec.md
 * §2 Case C, where a cholesterol of 245 sits on particle data that is clean.
 *
 * ── Why this is a model and not part of the component ──────────────────────
 * The bands and the resolved patterns are read by the panel, by the PDF height
 * estimate and (eventually) by the AI export. Thyroid keeps its band metadata
 * in its component and its patterns in ThyroidAnalysis; putting both here keeps
 * one answer per test instead of three callers each deriving their own.
 */

// ── Marker states ───────────────────────────────────────────────────────────

/**
 * Where one marker sits, in the vocabulary the pattern Logic column uses:
 * ▲ above optimal, ▼ below optimal, = optimal, and absent for untested.
 *
 * `high`/`low` here are about OPTIMAL, and they feed the AREA LADDER's
 * "possible" rung. The PATTERNS do not read them: they read the lab range
 * instead, for the reason set out above `Ctx` below.
 */
export type MarkerState = 'high' | 'low' | 'optimal' | 'absent';

export type LipidMarkerKey =
  | 'ldlParticleNumber'
  | 'hdlParticleNumber'
  | 'smallLdlParticleNumber'
  | 'ldlSize'
  | 'largeVldlParticleNumber'
  | 'largeHdlParticleNumber'
  | 'vldlSize'
  | 'hdlSize'
  | 'lpIrScore'
  | 'cholesterol'
  | 'ldl'
  | 'hdl'
  | 'triglycerides'
  | 'triglyceridesHDLRatio'
  | 'apolipoproteinA1'
  | 'apolipoproteinB'
  | 'apoBapoA1Ratio'
  | 'lipoproteinA'
  | 'vLDL';

const MARKER_KEYS: LipidMarkerKey[] = [
  'ldlParticleNumber', 'hdlParticleNumber', 'smallLdlParticleNumber', 'ldlSize',
  'largeVldlParticleNumber', 'largeHdlParticleNumber', 'vldlSize', 'hdlSize',
  'lpIrScore', 'cholesterol', 'ldl', 'hdl', 'triglycerides',
  'triglyceridesHDLRatio', 'apolipoproteinA1', 'apolipoproteinB',
  'apoBapoA1Ratio', 'lipoproteinA', 'vLDL',
];

export type LipidMarkerStates = Record<LipidMarkerKey, MarkerState>;

/**
 * The short name each marker shows in a pattern's ▲/▼ row.
 *
 * Abbreviated, the way the Glucose rows are, and the way the REPORT prints
 * them: a practitioner reading this beside the bar graphs is matching
 * "Small LDL-P" to a bar labelled "Small LDL-P".
 */
export const MARKER_ABBREV: Record<LipidMarkerKey, string> = {
  ldlParticleNumber: 'LDL-P',
  hdlParticleNumber: 'HDL-P',
  smallLdlParticleNumber: 'Small LDL-P',
  ldlSize: 'LDL Size',
  largeVldlParticleNumber: 'Large VLDL-P',
  largeHdlParticleNumber: 'Large HDL-P',
  vldlSize: 'VLDL Size',
  hdlSize: 'HDL Size',
  lpIrScore: 'LP-IR',
  cholesterol: 'Chol',
  ldl: 'LDL-C',
  hdl: 'HDL-C',
  triglycerides: 'TG',
  triglyceridesHDLRatio: 'TG/HDL',
  apolipoproteinA1: 'ApoA-1',
  apolipoproteinB: 'ApoB',
  apoBapoA1Ratio: 'ApoB/ApoA-1',
  lipoproteinA: 'Lp(a)',
  vLDL: 'VLDL',
};

/** Raw values alongside the states, for the rules that compare to a cut-off. */
export type LipidMarkerValues = Partial<Record<LipidMarkerKey, number>>;

export function lipidMarkerStates(ba: BloodworkAnalysis): {
  states: LipidMarkerStates;
  values: LipidMarkerValues;
  outsideLab: Partial<Record<LipidMarkerKey, 'high' | 'low'>>;
} {
  const states = {} as LipidMarkerStates;
  const values: LipidMarkerValues = {};
  const outsideLab: Partial<Record<LipidMarkerKey, 'high' | 'low'>> = {};

  MARKER_KEYS.forEach(key => {
    const pm: any = (ba as any)[key];
    if (!pm || typeof pm.hasValue !== 'function' || !pm.hasValue()) {
      states[key] = 'absent';
      return;
    }
    values[key] = pm.value;
    if (typeof pm.isGreaterThanLab === 'function' && pm.isGreaterThanLab()) {
      outsideLab[key] = 'high';
    } else if (typeof pm.isLessThanLab === 'function' && pm.isLessThanLab()) {
      outsideLab[key] = 'low';
    }
    if (pm.isGreaterThanOptimal && pm.isGreaterThanOptimal()) {
      states[key] = 'high';
    } else if (pm.isStricklyLessThanOptimal && pm.isStricklyLessThanOptimal()) {
      states[key] = 'low';
    } else {
      states[key] = 'optimal';
    }
  });

  return { states, values, outsideLab };
}

// ── Areas and the ladder ────────────────────────────────────────────────────

export type LipidArea =
  | 'ParticleBurden'
  | 'ParticleSize'
  | 'TriglycerideVldl'
  | 'HdlCapacity'
  | 'InheritedRisk';

export const LIPID_AREA_ORDER: LipidArea[] = [
  'ParticleBurden',
  'ParticleSize',
  'TriglycerideVldl',
  'HdlCapacity',
  'InheritedRisk',
];

/** The finding label each area reads as when it is the worst one. */
export const LIPID_AREA_LABEL: Record<LipidArea, string> = {
  ParticleBurden: 'Raised Particle Burden',
  ParticleSize: 'Small Particle Pattern',
  TriglycerideVldl: 'Triglyceride-Rich Particle Rise',
  HdlCapacity: 'Reduced HDL Capacity',
  InheritedRisk: 'Raised Lipoprotein(a)',
};

/**
 * The short name on the ranked bar, each naming THE MARKER IT MAPS TO.
 *
 * ⚠ THE PARENTHETICAL IS THE POINT. These were bare concept names until
 * 2026-10-26, and the panel sits beside the marker bar graphs: Maryann, reading
 * it, "'particle size' should be 'average particle size' (LDL size), so they
 * know that maps to the LDL size bar graph ... HDL capacity, I don't know what
 * that maps to. Particle burden, I don't know what that maps to."
 *
 * Each parenthetical is the area's LEAD marker from AREA_MARKERS below, the one
 * whose deviation usually sets the band, not the whole list. Measured at 10.5px
 * weight 600 against the longest band word each can carry: the widest row,
 * "Triglycerides & VLDL (LP-IR)" beside "not measured", comes to 215px inside
 * the 262px column.
 */
export const LIPID_AREA_SHORT: Record<LipidArea, string> = {
  ParticleBurden: 'Particle Burden (LDL-P)',
  ParticleSize: 'Average Particle Size (LDL Size)',
  TriglycerideVldl: 'Triglycerides & VLDL (LP-IR)',
  HdlCapacity: 'HDL Capacity (HDL-P)',
  InheritedRisk: 'Inherited Risk (Lp(a))',
};

/**
 * Each area's OWN markers. An area is assessed when any of them is measured,
 * and its band is the worst deviation among them.
 *
 * ⚠ AREA 3 DOES NOT LIST ITS COMPONENTS. LP-IR is computed FROM large VLDL-P,
 * small LDL-P, large HDL-P, VLDL size, LDL size and HDL size (Shalaurova 2014,
 * PMID: 24959989). Listing the score AND its six inputs would count the same
 * measurement seven times and push the area to "likely" on a single mild
 * abnormality. So the own-marker list carries the score plus the two markers
 * that are NOT inside it, and `AREA_MARKERS_WITHOUT_LPIR` is used instead when
 * no LP-IR was reported. See lipid-patterns-spec.md §6.
 */
const AREA_MARKERS: Record<LipidArea, LipidMarkerKey[]> = {
  ParticleBurden: ['ldlParticleNumber', 'apolipoproteinB', 'ldl'],
  ParticleSize: ['ldlSize', 'smallLdlParticleNumber'],
  TriglycerideVldl: ['lpIrScore', 'triglycerides', 'triglyceridesHDLRatio'],
  HdlCapacity: ['hdlParticleNumber', 'largeHdlParticleNumber', 'hdlSize', 'hdl', 'apolipoproteinA1'],
  InheritedRisk: ['lipoproteinA', 'apoBapoA1Ratio'],
};

/** Area 3's own markers when LP-IR is absent: its components stand in. */
const AREA_MARKERS_WITHOUT_LPIR: LipidMarkerKey[] = [
  'largeVldlParticleNumber', 'vldlSize', 'triglycerides', 'triglyceridesHDLRatio',
];

/**
 * The direction each area flags in. Every one is single-sided, which is also
 * what useRDWChart / useEGFRChart encode on the PanelFields.
 *
 * ParticleSize is the one that needs saying: a LARGE particle is the good end,
 * so the area flags on a low LDL Size, while its other own marker (small LDL-P,
 * a count) flags HIGH. That is why this is per area and the per-marker chart
 * direction is consulted rather than assumed.
 */
const AREA_FLAG_DIRECTION: Record<LipidArea, 'low' | 'high'> = {
  ParticleBurden: 'high',
  ParticleSize: 'low',
  TriglycerideVldl: 'high',
  HdlCapacity: 'low',
  InheritedRisk: 'high',
};

/** Markers whose adverse direction is the opposite of their area's. */
const MARKER_ADVERSE_DIRECTION: Partial<Record<LipidMarkerKey, 'low' | 'high'>> = {
  smallLdlParticleNumber: 'high', // a COUNT inside a size area
};

/**
 * The band an area sits in, named for CARDIOVASCULAR RISK rather than for
 * likelihood.
 *
 * ⚠ THIS IS NOT THE THYROID VOCABULARY, and the difference is the point.
 * Thyroid, Glucose and Adrenal say "highly likely", which is right when the
 * panel is INFERRING something nobody measured: a conversion problem, a
 * dysfunction. Nothing here is inferred. Particle burden is not a hidden
 * condition that might be present; it is LDL-P, a number on the report. Saying
 * "Particle Burden: highly likely" asks the reader what is likely, and there is
 * no answer.
 *
 * Labcorp prints the axis these markers actually sit on, on every interpretive
 * table: `<--Lower CVD Risk   Higher CVD Risk-->`. That is what the bars encode,
 * so that is what they are named for (Maryann, 2026-10-23, reviewing the first
 * build).
 *
 * The SCORES are unchanged: they are bar lengths and the ladder's rungs, and
 * they still come from the same five steps the other panels use.
 */
export type LipidBand =
  | 'notMeasured'
  | 'lower'
  | 'moderate'
  | 'higher'
  | 'highest';

export const LIPID_BAND_SCORE: Record<LipidBand, number | null> = {
  notMeasured: null,
  lower: 25,
  moderate: 60,
  higher: 80,
  highest: 95,
};

/*
 * LABCORP'S OWN LADDER WORDS, taken off the LDL-P row it prints on every
 * report: `Low < 1000 / Moderate 1000-1299 / Borderline-High 1300-1599 /
 * High 1600-2000 / Very High > 2000`.
 *
 * The first pass said "higher risk" and "highest risk" for the top two, which
 * are a syllable apart and read as the same thing (Maryann, 2026-10-23). These
 * are four words the practitioner already reads on the report, and the gap
 * between "borderline-high" and "high" is one anybody can see.
 */
export const LIPID_BAND_LABEL: Record<LipidBand, string> = {
  notMeasured: 'not measured',
  lower: 'low',
  moderate: 'moderate',
  higher: 'borderline-high',
  highest: 'high',
};

/** Labcorp's own axis caption, printed over every interpretive table. */
export const LIPID_RISK_AXIS_LOW = 'Lower CVD risk';
export const LIPID_RISK_AXIS_HIGH = 'Higher CVD risk';

// ── Patterns ────────────────────────────────────────────────────────────────

/**
 * What a pattern does to its area's band.
 *
 *   flag  — the area is highly likely. A finding in its own right.
 *   hold  — the area is held at lessLikely and the box EXPLAINS why, even
 *           though the bar is green. This is the effect Thyroid lacks: three of
 *           these exist to say a red standard-lipid marker is not carried by
 *           the particle data.
 *   note  — changes no band. Usually "this was not measured".
 */
export type LipidPatternEffect = 'flag' | 'hold' | 'note';

export interface LipidPattern {
  /** FurtherConsiderations categoryId. */
  id: number;
  /** Spec reference, e.g. "1.4". */
  ref: string;
  area: LipidArea;
  effect: LipidPatternEffect;
  title: string;
  /** The client-facing "consistent with" line. */
  consistentWith: string;
  /**
   * WHICH markers the row cites. The DIRECTION is not stored, because it is not
   * a property of the pattern: see `resolvePatternMarkers`.
   */
  markers: LipidMarkerKey[];
}

/*
 * PATTERN LOGIC READS THE LAB RANGE, NOT OPTIMAL. This is the one place the
 * section departs from the house ▲/▼ convention, and it is deliberate.
 *
 * For eight of the nine NMR markers, optimal is authored at the lab's 25th
 * percentile (lipid-patterns-spec.md §3a). That is right for a bar color and
 * for the ladder's "possible" rung, and far too narrow for a pattern: DK's
 * small LDL-P of 297 sits between the 25th and 50th percentiles, so under an
 * optimal reading `= Small LDL-P` would be false and "More and Larger
 * Particles" would not fire for the very client it was written from. Most
 * clients are above the 25th percentile on most of these, so every `=`
 * condition would be near-unsatisfiable.
 *
 * So ▲ / ▼ / = here mean outside / outside / inside THE LAB RANGE, which is
 * also what the practitioner reads off the report's own High and Low flags.
 * The ladder still uses both rungs (outside lab → likely, outside optimal →
 * possible), so nothing is lost: optimal still colors the bar, and the pattern
 * speaks about what the lab flagged.
 */
type Ctx = {
  /** Optimal-relative states. Kept for the ladder and for marker rows. */
  s: LipidMarkerStates;
  v: LipidMarkerValues;
  lab: Partial<Record<LipidMarkerKey, 'high' | 'low'>>;
  /** ▲ — outside the lab range on the high side. */
  hi: (k: LipidMarkerKey) => boolean;
  /** ▼ — outside the lab range on the low side. */
  lo: (k: LipidMarkerKey) => boolean;
  /** = — tested and inside the lab range. */
  eq: (k: LipidMarkerKey) => boolean;
  /** Not tested at all. */
  absent: (k: LipidMarkerKey) => boolean;
  /** LDL-P at or over the MESA split, which is what "▲ LDL-P (≥1000)" means. */
  countOver: boolean;
  /**
   * Small LDL-P over Labcorp's printed 527 nmol/L midpoint: the grid's SECOND
   * axis. Area 1's patterns key on this rather than on `hi()` so the box and
   * the grid above it always divide the client the same way, even if the
   * BloodworkRange row for Small LDL-P is ever authored away from 527.
   */
  smallOver: boolean;
  /** Small LDL-P was reported at all, so the second axis could be read. */
  smallTested: boolean;
  patternA: boolean;
  patternB: boolean;
};

/**
 * The direction shown beside a marker in a pattern row.
 *
 * ⚠ READ FROM THE CLIENT'S VALUE, NOT FROM THE PATTERN. This was authored per
 * pattern for about a day, as "the signature direction the pattern asserts",
 * and it was wrong in the way that matters most: the panel sits BESIDE the bar
 * graphs. "More and Larger LDL Particles" drew ▼ Small LDL-P because the
 * pattern means "small particles are not the story here", while the bar two
 * inches away showed Small LDL-P at 297 against an optimal max of 117 — above
 * optimal, visibly (Maryann, 2026-10-23). An arrow that contradicts the bar
 * next to it costs the reader their trust in the whole row.
 *
 * So: ▲ above optimal, ▼ below optimal, and a neutral mark inside optimal,
 * matching the OPTIMAL band of the bar beside it. A marker the client never
 * had is dropped from the row rather than drawn without a value.
 */
export type MarkerArrow = 'up' | 'down' | 'level';

export interface ResolvedMarker {
  key: LipidMarkerKey;
  label: string;
  direction: MarkerArrow;
}

export function resolvePatternMarkers(
  pattern: LipidPattern,
  states: LipidMarkerStates,
): ResolvedMarker[] {
  return pattern.markers
    .filter(key => states[key] !== 'absent')
    .map(key => ({
      key,
      label: MARKER_ABBREV[key],
      direction:
        states[key] === 'high'
          ? ('up' as const)
          : states[key] === 'low'
          ? ('down' as const)
          : ('level' as const),
    }));
}

/**
 * The 21 rows, in spec order. Each `when` is the Logic column and nothing more:
 * a rule that needs a marker absent says so, and a rule that needs one optimal
 * says `=` rather than "not high".
 */
const PATTERNS: (LipidPattern & { when: (c: Ctx) => boolean })[] = [
  // ── Area 1: Particle Burden ───────────────────────────────────────────────
  {
    id: 97000, ref: '1.1', area: 'ParticleBurden', effect: 'flag',
    title: 'More Total LDL Particles, Fewer Small',
    /*
     * WHY A HIGH COUNT STILL COUNTS WHEN THE PARTICLES ARE LARGE. The obvious
     * objection to this pattern is that Pattern A is the "good" pattern, so a
     * raised LDL-P with large particles should be benign. The evidence says
     * otherwise, and it is recent and large:
     *
     *   Morze 2025, Eur Heart J, UK Biobank, 207,368 adults with no prior
     *   atherosclerotic disease, diabetes or lipid therapy: "Particle diameter
     *   or size subclasses were not associated with CAD after apoB-P
     *   adjustment", and risk is "largely unaffected by the major particle type
     *   (VLDL, LDL) or size" (PMID: 40289348).
     *
     *   Mora 2007, MESA: with BOTH subclasses in the model, large LDL-P was
     *   independently associated with carotid IMT (36.6 um per 1 SD, p<0.001)
     *   while LDL size was not (p=0.10) (PMID: 16765964).
     *
     * ⚠ NOT because every particle is equally dangerous. That was the first
     * draft of this comment and it is wrong. PER PARTICLE, small dense LDL is
     * worse on several counts: it is more susceptible to oxidation, and its
     * oxidative modification is described as the earliest event in
     * atherogenesis (Vekic 2023, PMID: 38001900); modified and small dense LDL
     * favour subendothelial accumulation of cholesterol (Benitez 2024, PMID:
     * 39713216); and sdLDL carries a different proteome, enriched in apoC-III
     * (Diffenderfer 2014, PMID: 24811298). Large LDL is not immune to oxidation
     * either, just less prone to it.
     *
     * The reconciliation is that the two facts live at different levels. Size
     * matters per particle; COUNT matters more across a population, and the two
     * travel together — the small-dense phenotype is by definition a lot of
     * particles each carrying less cholesterol. So counting the particles
     * already absorbs most of the size effect, which is why size drops out of
     * the model once apoB count is in it rather than because it is inert.
     *
     * What that means for a high count of LARGE particles: each one is somewhat
     * less likely to stick than a small one, and there are 1585 of them. The
     * outcome data tracks the count.
     */
    consistentWith:
      'A raised number of cholesterol-carrying particles, with the small-particle count at or below Labcorp’s 527 nmol/L midpoint. The count is what the research follows: particle size adds little once the number of particles is known',
    /*
     * ⚠ THE MARKERS ARE THE GRID'S TWO AXES, AND MEAN DIAMETER IS NOT ONE.
     * This rule read `countOver && patternA && eq(small)` for a day, which made
     * the title say "Larger" off the mean while the grid cell two inches above
     * said "Fewer Small" off the count — one client, two vocabularies, and a
     * third marker row for a quantity neither of them was dividing on
     * (Maryann, 2026-10-23). Area 2 is where mean diameter is assessed.
     *
     * ⚠ THE BOX AND THE ARROW USE DIFFERENT REFERENCES, DELIBERATELY. This box
     * places the client against Labcorp's PRINTED midpoint (527), while the
     * arrow beside Small LDL-P is read off the client's OPTIMAL band so that it
     * agrees with the bar graph next to it. DK sits in the lower row at 297 and
     * still draws ▲, because 297 is over an optimal max of 117. Both readings
     * are true, of different reference points. Kept out of the box text, which
     * stays short.
     */
    markers: ['ldlParticleNumber', 'smallLdlParticleNumber'],
    when: c => c.countOver && c.smallTested && !c.smallOver,
  },
  {
    id: 97005, ref: '1.2', area: 'ParticleBurden', effect: 'flag',
    title: 'More Total LDL Particles, More Small',
    consistentWith:
      'A raised number of cholesterol-carrying particles, and the small-particle count is above Labcorp’s 527 nmol/L midpoint as well',
    // The other half of the same split, so 1.1 and 1.2 cannot both fire.
    markers: ['ldlParticleNumber', 'smallLdlParticleNumber'],
    when: c => c.countOver && c.smallOver,
  },
  {
    id: 97010, ref: '1.3', area: 'ParticleBurden', effect: 'flag',
    title: 'Particle Count Above What Cholesterol Shows',
    consistentWith:
      'A particle count higher than the cholesterol result alone would suggest',
    markers: ['ldlParticleNumber', 'ldl'],
    when: c => c.countOver && c.eq('ldl'),
  },
  {
    id: 97015, ref: '1.4', area: 'ParticleBurden', effect: 'hold',
    title: 'Cholesterol Above What the Particle Count Shows',
    consistentWith:
      'A cholesterol result above range, carried on a particle count and particle size that are both unremarkable',
    markers: ['cholesterol', 'ldl', 'ldlParticleNumber', 'smallLdlParticleNumber', 'ldlSize'],
    when: c =>
      (c.hi('cholesterol') || c.hi('ldl'))
      && !c.absent('ldlParticleNumber')
      && !c.countOver
      && c.eq('smallLdlParticleNumber')
      && c.patternA,
  },

  // ── Area 2: Particle Size Pattern ────────────────────────────────────────
  {
    id: 97100, ref: '2.1', area: 'ParticleSize', effect: 'flag',
    title: 'Small LDL Particle Pattern',
    consistentWith:
      'Small, dense cholesterol-carrying particles alongside triglyceride-rich markers',
    markers: ['ldlSize', 'smallLdlParticleNumber', 'triglycerides', 'triglyceridesHDLRatio', 'lpIrScore'],
    when: c =>
      c.patternB
      && c.hi('smallLdlParticleNumber')
      && (c.hi('triglycerides') || c.hi('triglyceridesHDLRatio') || c.hi('lpIrScore')),
  },
  {
    id: 97105, ref: '2.2', area: 'ParticleSize', effect: 'flag',
    title: 'Small LDL Particle Pattern without a Triglyceride Rise',
    consistentWith:
      'Small, dense particles without the triglyceride markers that usually accompany them',
    markers: ['ldlSize', 'smallLdlParticleNumber', 'triglycerides', 'triglyceridesHDLRatio', 'lpIrScore'],
    when: c =>
      c.patternB
      && c.hi('smallLdlParticleNumber')
      && !c.hi('triglycerides')
      && !c.hi('triglyceridesHDLRatio')
      && !c.hi('lpIrScore'),
  },
  {
    id: 97110, ref: '2.3', area: 'ParticleSize', effect: 'hold',
    title: 'Small LDL Particles with a Particle Count in Range',
    consistentWith:
      'Small particle size carried on a particle count that is not raised',
    markers: ['ldlSize', 'ldlParticleNumber', 'smallLdlParticleNumber'],
    when: c =>
      c.patternB
      && !c.absent('ldlParticleNumber')
      && !c.countOver
      && c.eq('smallLdlParticleNumber'),
  },
  {
    id: 97115, ref: '2.4', area: 'ParticleSize', effect: 'note',
    title: 'Large LDL Particle Pattern',
    consistentWith: 'Large particle size with a particle count in range',
    markers: ['ldlSize', 'smallLdlParticleNumber', 'ldlParticleNumber'],
    when: c =>
      c.patternA
      && c.eq('smallLdlParticleNumber')
      && !c.absent('ldlParticleNumber')
      && !c.countOver,
  },

  {
    /*
     * The discordance the Particle Map cannot show. Mean size sits in the
     * Pattern A range while the small-particle COUNT is above the lab's own
     * interval, so the hero fills a "Larger" cell and the Particle Size bar
     * goes orange with nothing to explain it (found 2026-10-23).
     *
     * A real finding rather than a caveat: small dense LDL is the subclass with
     * the per-particle evidence behind it (Vekic 2023, PMID: 38001900;
     * Diffenderfer 2014, PMID: 24811298), and a mean diameter can stay large
     * while a meaningful number of particles sit in the small subclass.
     *
     * Id 97125 rather than the retired 97120: that id belonged to the "Particle
     * Size Not Measured" note, and reusing a retired categoryId would collide
     * in FurtherConsiderations if those rows are ever loaded from the handover
     * CSV that still carries it.
     */
    id: 97125, ref: '2.5', area: 'ParticleSize', effect: 'flag',
    title: 'Raised Small LDL Particle Count with Large Average Size',
    consistentWith:
      'A raised number of small, dense particles even though the average particle size measures large',
    markers: ['smallLdlParticleNumber', 'ldlSize'],
    when: c => c.hi('smallLdlParticleNumber') && c.patternA,
  },

  // ── Area 3: Triglyceride and VLDL Handling ───────────────────────────────
  {
    id: 97200, ref: '3.1', area: 'TriglycerideVldl', effect: 'flag',
    title: 'Lipoprotein Pattern of Reduced Insulin Sensitivity',
    consistentWith:
      'A lipoprotein pattern reported alongside reduced insulin sensitivity',
    markers: ['lpIrScore', 'largeVldlParticleNumber', 'vldlSize', 'smallLdlParticleNumber', 'largeHdlParticleNumber', 'ldlSize', 'hdlSize'],
    when: c => {
      if (!c.hi('lpIrScore')) return false;
      const supporting = [
        c.hi('largeVldlParticleNumber'),
        c.hi('vldlSize'),
        c.hi('smallLdlParticleNumber'),
        c.lo('largeHdlParticleNumber'),
        c.lo('ldlSize'),
        c.lo('hdlSize'),
      ].filter(Boolean).length;
      return supporting >= 2;
    },
  },
  {
    id: 97205, ref: '3.2', area: 'TriglycerideVldl', effect: 'flag',
    title: 'Triglyceride-Rich Particle Rise',
    consistentWith: 'Larger, more numerous triglyceride-rich particles',
    markers: ['largeVldlParticleNumber', 'vldlSize', 'triglycerides', 'triglyceridesHDLRatio'],
    when: c =>
      // LP-IR absent on purpose: when the score IS reported, 3.1 owns this
      // area and reading the components as well would double-count them.
      c.absent('lpIrScore')
      && c.hi('largeVldlParticleNumber')
      && c.hi('vldlSize')
      && (c.hi('triglycerides') || c.hi('triglyceridesHDLRatio')),
  },
  {
    id: 97210, ref: '3.3', area: 'TriglycerideVldl', effect: 'hold',
    title: 'Triglyceride Rise without the Particle Pattern',
    consistentWith:
      'A triglyceride rise that the particle measurements do not carry',
    markers: ['triglycerides', 'largeVldlParticleNumber', 'vldlSize', 'lpIrScore'],
    when: c =>
      c.hi('triglycerides')
      && c.eq('largeVldlParticleNumber')
      && c.eq('vldlSize')
      && !c.hi('lpIrScore'),
  },
  // ── Area 4: HDL Capacity ─────────────────────────────────────────────────
  {
    id: 97300, ref: '4.1', area: 'HdlCapacity', effect: 'flag',
    title: 'Reduced HDL Particle Capacity',
    consistentWith: 'Fewer and smaller HDL particles',
    markers: ['hdlParticleNumber', 'largeHdlParticleNumber', 'hdlSize'],
    when: c =>
      c.lo('hdlParticleNumber') && c.lo('largeHdlParticleNumber') && c.lo('hdlSize'),
  },
  {
    id: 97305, ref: '4.2', area: 'HdlCapacity', effect: 'flag',
    title: 'Reduced Large HDL with Total HDL in Range',
    consistentWith:
      'A total HDL particle count in range carried by fewer of the large HDL particles',
    markers: ['hdlParticleNumber', 'largeHdlParticleNumber'],
    when: c => c.eq('hdlParticleNumber') && c.lo('largeHdlParticleNumber'),
  },
  {
    id: 97310, ref: '4.3', area: 'HdlCapacity', effect: 'flag',
    title: 'HDL Particles Below What HDL Cholesterol Shows',
    consistentWith:
      'An HDL cholesterol result in range with an HDL particle count below range',
    markers: ['hdl', 'hdlParticleNumber'],
    when: c => c.eq('hdl') && c.lo('hdlParticleNumber'),
  },
  {
    id: 97315, ref: '4.4', area: 'HdlCapacity', effect: 'hold',
    title: 'HDL Cholesterol Above Range with HDL Particles in Range',
    consistentWith:
      'An HDL cholesterol result above range, carried on HDL particle measurements that are unremarkable',
    markers: ['hdl', 'hdlParticleNumber', 'largeHdlParticleNumber'],
    when: c =>
      c.hi('hdl') && c.eq('hdlParticleNumber') && c.eq('largeHdlParticleNumber'),
  },

  // ── Area 5: Inherited Particle Risk ──────────────────────────────────────
  {
    id: 97400, ref: '5.1', area: 'InheritedRisk', effect: 'flag',
    title: 'Raised Lipoprotein(a)',
    consistentWith:
      'A raised lipoprotein(a), which is largely set by genetics and changes little over time',
    markers: ['lipoproteinA'],
    when: c => c.hi('lipoproteinA') && !c.countOver,
  },
  {
    id: 97405, ref: '5.2', area: 'InheritedRisk', effect: 'flag',
    title: 'Raised Lipoprotein(a) with a Raised Particle Count',
    consistentWith: 'A raised lipoprotein(a) alongside a raised particle count',
    markers: ['lipoproteinA', 'ldlParticleNumber'],
    when: c => c.hi('lipoproteinA') && c.countOver,
  },
];

/**
 * Precedence inside each area: the FIRST matching pattern wins, and the order
 * below is the order above. Each area returns at most one pattern, the way the
 * Liver spec's five independent resolvers do, so several areas can be red at
 * once without any of them stealing another's box.
 *
 * NO EXCEPTIONS to one-per-area. There used to be one: 3.4, a note saying the
 * LP-IR Score was read from a sample with no fasting status recorded, which had
 * to sit BESIDE 3.1 because it was a caveat on it rather than an alternative.
 * It was removed on 2026-10-23 (Maryann): `Bloodwork` has no fasting field, so
 * the note fired on every test carrying a score, and the standing assumption is
 * that a client having labs drawn is fasting. The lab's condition is now a
 * single footnote on the panel instead of a pattern box per test.
 */

export interface LipidAreaResult {
  area: LipidArea;
  band: LipidBand;
  score: number | null;
  /**
   * Set only where the LAB publishes a scale for the area's driving marker, so
   * the bar can show the VALUE rather than a rung.
   *
   * Particle Burden is the one such area today: Labcorp prints five LDL-P bands
   * on every report (Low / Moderate / Borderline-High / High / Very High), so a
   * bar that could only sit at four rungs was drawing LDL-P 1000 and LDL-P 2400
   * identically (Maryann, 2026-10-23). The other four areas have no published
   * scale and keep the ladder.
   */
  scale?: { percent: number; label: string; edges: number[] };
  /** The pattern that owns this area, when one matched. */
  pattern?: LipidPattern;
  /** Why the band is what it is, for the practitioner tab. */
  because: string;
}

/** A matched pattern plus the ▲/▼ row resolved against THIS client. */
export interface ResolvedPattern {
  pattern: LipidPattern;
  markers: ResolvedMarker[];
}

export interface LipidAreaAnalysis {
  areas: LipidAreaResult[];
  /** Every pattern that matched, in area order. */
  patterns: LipidPattern[];
  /** The same patterns with their marker rows resolved for display. */
  resolved: ResolvedPattern[];
  /** True when at least one area was assessed. */
  anyAssessed: boolean;
}

/** Labcorp's five LDL-P bands onto the four colors the bars already use. */
function bandForLdlPScale(percent: number): LipidBand {
  if (percent <= 20) return 'lower';          // Low
  if (percent <= 40) return 'moderate';       // Moderate
  if (percent <= 60) return 'higher';         // Borderline-High
  return 'highest';                           // High and Very High
}

function bandFromMarkers(
  area: LipidArea,
  s: LipidMarkerStates,
  lab: Partial<Record<LipidMarkerKey, 'high' | 'low'>>,
  markers: LipidMarkerKey[],
): { band: LipidBand; because: string } {
  const tested = markers.filter(k => s[k] !== 'absent');
  if (!tested.length) {
    return { band: 'notMeasured', because: 'None of this area’s markers were tested.' };
  }
  const adverse = (k: LipidMarkerKey) =>
    MARKER_ADVERSE_DIRECTION[k] ?? AREA_FLAG_DIRECTION[area];

  const outsideLab = tested.filter(k => lab[k] === adverse(k));
  if (outsideLab.length) {
    return {
      band: 'higher',
      because: `Outside the lab range: ${outsideLab.join(', ')}.`,
    };
  }
  const outsideOptimal = tested.filter(k => s[k] === adverse(k));
  if (outsideOptimal.length) {
    return {
      band: 'moderate',
      because: `Outside optimal but within the lab range: ${outsideOptimal.join(', ')}.`,
    };
  }
  return {
    band: 'lower',
    because: `Tested and within optimal: ${tested.join(', ')}.`,
  };
}

export function resolveLipidAreas(ba?: BloodworkAnalysis): LipidAreaAnalysis | null {
  if (!ba || !ba.bloodwork) return null;
  const { states: s, values: v, outsideLab: lab } = lipidMarkerStates(ba);

  const countOver =
    v.ldlParticleNumber != null && v.ldlParticleNumber >= LDL_P_COUNT_SPLIT;
  const smallTested = v.smallLdlParticleNumber != null;
  /*
   * STRICTLY greater, matching the map's own comparison (LipidParticleMap) and
   * the row label "Small ≤527": Labcorp prints ≤527 as the reference, so 527
   * itself belongs in the lower row. A >= here would put one value in the lower
   * row of the grid and in the upper pattern of the box.
   */
  const smallOver =
    v.smallLdlParticleNumber != null
    && v.smallLdlParticleNumber > SMALL_LDL_P_SPLIT;
  const patternA =
    v.ldlSize != null && v.ldlSize >= PATTERN_A_FLOOR_NM;
  const patternB = v.ldlSize != null && v.ldlSize < PATTERN_A_FLOOR_NM;

  const ctx: Ctx = {
    s, v, lab, countOver, smallOver, smallTested, patternA, patternB,
    hi: k => lab[k] === 'high',
    lo: k => lab[k] === 'low',
    eq: k => s[k] !== 'absent' && lab[k] === undefined,
    absent: k => s[k] === 'absent',
  };

  const matched = PATTERNS.filter(p => p.when(ctx));

  const areas: LipidAreaResult[] = LIPID_AREA_ORDER.map(area => {
    // Area 3 swaps its own-marker list when LP-IR is absent, so its components
    // can still assess it without ever being counted beside the score.
    const own =
      area === 'TriglycerideVldl' && s.lpIrScore === 'absent'
        ? AREA_MARKERS_WITHOUT_LPIR
        : AREA_MARKERS[area];

    const owner = matched.find(p => p.area === area);
    const marker = bandFromMarkers(area, s, lab, own);

    /*
     * The published-scale override. When LDL-P itself is on the test, Particle
     * Burden's bar reads the VALUE on Labcorp's own bands rather than a rung,
     * and the label is Labcorp's own word for that band. A matched pattern
     * still shows its box; it no longer decides the bar, because the bar is now
     * reporting a measurement.
     */
    if (area === 'ParticleBurden' && v.ldlParticleNumber != null) {
      const percent = ldlPScalePercent(v.ldlParticleNumber);
      const label = ldlPBandLabel(v.ldlParticleNumber);
      /*
       * The owner's sentence is KEPT, in the same words the ladder arms below
       * use. The bar now reports a measurement, but the reason a clean bar sits
       * under two red standard-lipid bars is the held pattern, and that is the
       * whole point of the hold (Case C, Ian).
       */
      const ownerNote = !owner
        ? ''
        : owner.effect === 'hold'
          ? ` Held: pattern ${owner.ref} (${owner.title}) explains the marker that would otherwise raise this area.`
          : ` Pattern ${owner.ref} (${owner.title}) points to this area.`;
      return {
        area,
        band: bandForLdlPScale(percent),
        score: percent,
        scale: { percent, label, edges: LDL_P_SCALE_EDGES },
        ...(owner ? { pattern: owner } : {}),
        because:
          `LDL-P ${v.ldlParticleNumber} nmol/L sits in Labcorp's ${label} band.` +
          ownerNote,
      };
    }

    if (owner?.effect === 'flag') {
      return {
        area, band: 'highest', score: LIPID_BAND_SCORE.highest,
        pattern: owner,
        because: `Pattern ${owner.ref} (${owner.title}) points to this area.`,
      };
    }
    if (owner?.effect === 'hold') {
      return {
        area,
        band: marker.band === 'notMeasured' ? 'notMeasured' : 'lower',
        score: marker.band === 'notMeasured' ? null : LIPID_BAND_SCORE.lower,
        pattern: owner,
        because: `Held: pattern ${owner.ref} (${owner.title}) explains the marker that would otherwise raise this area.`,
      };
    }
    return {
      area, band: marker.band, score: LIPID_BAND_SCORE[marker.band],
      ...(owner ? { pattern: owner } : {}),
      because: marker.because,
    };
  });

  return {
    areas,
    patterns: matched,
    resolved: matched.map(pattern => ({
      pattern,
      markers: resolvePatternMarkers(pattern, s),
    })),
    anyAssessed: areas.some(a => a.band !== 'notMeasured'),
  };
}

/** Areas worst-first, for the ranked bars. Ties keep spec order. */
export function rankedLipidAreas(analysis: LipidAreaAnalysis): LipidAreaResult[] {
  return [...analysis.areas].sort(
    (a, b) =>
      (b.score ?? -1) - (a.score ?? -1)
      || LIPID_AREA_ORDER.indexOf(a.area) - LIPID_AREA_ORDER.indexOf(b.area),
  );
}

/** Every pattern in the set, for the handover CSV and the drift tests. */
export const LIPID_PATTERNS: LipidPattern[] = PATTERNS.map(
  ({ when, ...rest }) => rest,
);
